import { Dashboard, UserBadge, Admin, IdInfo } from "@rsuite/icons";
import OthersIcon from "@rsuite/icons/Others";
import LocationIcon from "@rsuite/icons/Location";
import GridIcon from "@rsuite/icons/Grid";
import PeopleRuleIcon from "@rsuite/icons/PeopleRule";
import { useTranslation } from "react-i18next";
import { any } from "lodash/fp";
import {
  availableOnDayObjArr,
  foodTypeObjArr,
  IS_ALPHANUMERIC_REGEX,
  IS_ALPHANUMERIC_WITH_DASH_REGEX,
  IS_ALPHANUMERIC_WITH_DASH_SPACE_REGEX,
  IS_ALPHANUMERIC_WITH_SPACE_REGEX,
  IS_ALPHA_REGEX,
  sizeObjArr,
  statusObjArr,
  orderDayTypeObj,
  dateFormatObj,
  onOffArr,
  yesNoObj,
} from "../core/config/AppConstants";
import moment from "moment";
//const { t } = useTranslation();
function isEmptyVal(value: any) {
  return (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" && !value && Object.keys(value).length === 0) ||
    (typeof value === "string" && value.trim().length === 0)
  );
}

function formatDateOnly(date: Date | string) {
  if (!date) return null;

  const d = typeof date === "string" ? new Date(date) : date;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}
function isNotEmpty(value: any) {
  if (value === null || value === undefined) return false;
  if (typeof value === "object") return Object.keys(value).length > 0;
  if (typeof value === "string")
    return (
      value.trim().length > 0 &&
      value.trim() !== "null" &&
      value.trim() !== "undefined"
    );
  return value !== undefined && value !== null;
}

function isNotNull(value: any) {
  return value !== null && value !== undefined;
}

function removeHTMLTags(input: string): string {
  return input.replace(/<[^>]*>/g, ""); // This regular expression matches any HTML tag and removes it
}

function displayField(value: any) {
  return isNotEmpty(value) ? value : "-NA-";
}

function displayLimitedField(value: any, displayLen = 30) {
  let origVal = isNotEmpty(value) ? value : "-NA-";
  let displayVal = isNotEmpty(value) ? value : "-NA-";
  displayVal = displayVal.substring(0, displayLen);
  if (origVal.length > displayLen) {
    displayVal = displayVal + "...";
  }
  return displayVal;
}

function displayAmountField(value: any) {
  return isNotEmpty(value) ? parseFloat(value).toFixed(2) : "0.00";
}

const downloadImage = async (imageUrl: string, downloadFileName?: string) => {
  try {
    const response = await fetch(imageUrl, { mode: "no-cors" });
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = downloadFileName ?? "downloaded-image.png"; // Set desired filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl); // Cleanup
  } catch (error) {
    console.error("Error downloading the image:", error);
  }
};

function zeroFill(value: string, len: number) {
  const lng = value.length;
  let i: number = 0;
  let zero = "";
  if (!lng) return value;
  if (lng < len) {
    for (i = 0; i < len - lng; i++) {
      zero = zero + "0";
    }
  }
  return zero + value;
}

function processMinute(value: number): string {
  let processed = "";

  const hours = Math.trunc(value / 60);
  const minutes = value % 60;

  if (hours > 0) {
    processed += `${hours} Hr${hours > 1 ? "s" : ""}. `;
  }

  processed += `${minutes} Min${minutes > 1 ? "s" : ""}.`;

  return processed.trim();
}

function processSecond(value: number): string {
  let processed = "";

  const hours = Math.trunc(value / 3600);
  const minutes = Math.trunc((value % 3600) / 60);
  const seconds = value % 60;

  if (hours > 0) {
    processed += `${hours} Hr${hours > 1 ? "s" : ""}. `;
  }
  if (minutes > 0) {
    processed += `${minutes} Min${minutes > 1 ? "s" : ""}. `;
  }
  if (seconds > 0) {
    processed += `${seconds} Sec${seconds > 1 ? "s" : ""}.`;
  }

  return processed.trim();
}

function displayDateField(
  value: any,
  displayFormat = dateFormatObj.display_d_m_y,
  localFormat = true
) {
  if (isEmptyVal(value)) return "-NA-";
  // Assuming MySQL date format is 'YYYY-MM-DD HH:mm:ss'
  const utcMoment =
    localFormat === true
      ? moment.utc(value, "YYYY-MM-DD HH:mm:ss")
      : moment(value, "YYYY-MM-DD HH:mm:ss");

  if (utcMoment.isValid()) {
    // Convert to local time zone
    const localMoment = utcMoment.local();
    // Format the date according to the desired format
    return localFormat === true
      ? localMoment.format(displayFormat)
      : utcMoment.format(displayFormat);
  } else {
    console.error("Invalid MySQL date format");
    return "-NA-";
  }
}

function displayDayDateField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_day_d_m_y
) {
  return displayDateField(value, displayFormat, localFormat);
}

function displayDateTimeField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_d_m_y_h_m
) {
  return displayDateField(value, displayFormat, localFormat);
}

function displayTimeField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_h_m
) {
  return displayDateField(value, displayFormat, localFormat);
}

function displayDayDateTimeField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_day_d_m_y_h_m
) {
  return displayDateField(value, displayFormat, localFormat);
}

function displayYesNo(value: any) {
  return value == onOffArr.on ? yesNoObj.yes : yesNoObj.no;
}

function displayDateMonthField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_date_month
) {
  return displayDateField(value, displayFormat, localFormat);
}

function generateTimeOptions(interval: number = 5) {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += interval) {
      // Format values for 24-hour format
      const hour24 = String(h).padStart(2, "0");
      const minute = String(m).padStart(2, "0");

      // Convert to 12-hour format with AM/PM
      const period = h < 12 ? "AM" : "PM";
      const hour12 = String(h % 12 === 0 ? 12 : h % 12).padStart(2, "0"); // Ensuring 2-digit format

      times.push({
        label: `${hour12}:${minute} ${period}`, // Display in 12-hour format (zero-padded)
        value: `${hour24}:${minute}`, // Keep value in 24-hour format
      });
    }
  }
  return times;
}

function addLabelValue(
  valArr: any,
  lableIndex: string,
  valueIndex: string,
  labelName: string = "label",
  valueName: string = "value"
) {
  const results: any[] = [];
  valArr.forEach(function (value: any) {
    value[labelName] = value[lableIndex];
    value[valueName] = value[valueIndex].toString();
    results.push(value);
  });
  return results;
}

function getStdCodesFromCountryList(countryList: any) {
  const codes: any[] = [];
  let existsCodes: any = [];
  countryList.forEach(function (value: any) {
    if (
      isNotEmpty(value["country_std_code"]) &&
      !existsCodes.includes(value["country_std_code"])
    ) {
      existsCodes.push(value["country_std_code"]);
      codes.push(value);
    }
  });
  return codes;
}

function getCurrenciesFromCountryList(countryList: any) {
  const currencies: any[] = [];
  let existsCurrencies: any = [];
  countryList.forEach(function (value: any) {
    if (
      isNotEmpty(value["currency_code"]) &&
      !existsCurrencies.includes(value["currency_code"])
    ) {
      existsCurrencies.push(value["currency_code"]);
      currencies.push(value);
    }
  });
  return currencies;
}

function processProductTypeFields(productTypeFields: any) {
  const processedFileds: any[] = [];
  let existsCurrencies: any = [];
  productTypeFields.forEach(function (pField: any) {
    processedFileds[pField["field_name"]] = pField;
  });
  return processedFileds;
}

function getFoodTypeLabel(foodType: string) {
  let foodTypeVal = "";
  foodTypeObjArr.forEach(function (foodTypeObj: any) {
    if (foodTypeObj.value == foodType) {
      foodTypeVal = foodTypeObj.label;
    }
  });
  return foodTypeVal;
}

function getSizeLabel(size: string) {
  let sizeVal = "";
  sizeObjArr.forEach(function (sizeObj: any) {
    if (sizeObj.value == size) {
      sizeVal = sizeObj.label;
    }
  });
  return sizeVal;
}

function getLabelFor(val: string, labelObjArr: any) {
  let labelVal = "";
  labelObjArr.forEach(function (labelObj: any) {
    if (labelObj.value == val) {
      labelVal = labelObj.label;
    }
  });
  return labelVal;
}

function getMultiLabelFor(val: string, labelObjArr: any) {
  let labelArr: string[] = [];

  val.split(",").forEach((singleVal) => {
    let labelObj = labelObjArr.find(
      (labelObj: any) => labelObj.value === singleVal.trim()
    );
    if (labelObj) {
      labelArr.push(labelObj.label);
    }
  });

  return labelArr.join(", ");
}

function getPincodeForArea(areaId: string, areaObjArr: any) {
  let pincode = "";
  areaObjArr.forEach(function (areaObj: any) {
    if (areaObj.id == areaId && areaObj.pincode !== null) {
      pincode = areaObj.pincode;
    }
  });
  return pincode;
}

function getStdCodeForCountry(countyId: string, countryObjArr: any) {
  let stdCode = "";
  countryObjArr.forEach(function (cntObj: any) {
    if (cntObj.id == countyId && cntObj.country_std_code !== null) {
      stdCode = cntObj.country_std_code;
    }
  });
  return stdCode;
}

function getImageForTheme(
  themeId: string,
  themeObjArr: any,
  imageType: string = "preview_image_url"
) {
  let imageUrl = "";
  themeObjArr.forEach(function (thmObj: any) {
    if (thmObj.id == themeId && !isEmptyVal(thmObj[imageType])) {
      imageUrl = thmObj[imageType];
    }
  });
  return imageUrl;
}

function getAvailableDaysLabel(days: string) {
  let dayVal = "";

  if (isEmptyVal(days)) return dayVal;
  let daysArr = days.split(",");
  daysArr.forEach(function (day: any) {
    availableOnDayObjArr.forEach(function (dayObj: any) {
      if (dayObj.value == day) {
        dayVal = dayObj.label + " ";
      }
    });
  });
  return dayVal;
}

function getStatusLabel(status: any) {
  let statusVal = "";
  statusObjArr.forEach(function (statusObj: any) {
    if (statusObj.value == status) {
      statusVal = statusObj.label;
    }
  });
  return statusVal;
}

function getHourMinuteFormat(timeVal: any) {
  if (!timeVal || typeof timeVal !== "string") {
    return ""; // or null, depending on your UI needs
  }

  const timeArr = timeVal.split(":");

  let timeToReturn = "";

  if (timeArr[0]) timeToReturn = timeArr[0];
  if (timeArr[1]) timeToReturn = timeToReturn + ":" + timeArr[1];

  return timeToReturn;
}

function getMenuIcon(iconName: string) {
  let icon = null;
  if (!iconName) return icon;
  switch (iconName.toLowerCase()) {
    case "dashboard":
    case "home":
      icon = <Dashboard />;
      break;
    case "user":
      icon = <UserBadge />;
      break;
    case "role":
      icon = <Admin />;
      break;
    case "company":
      icon = <IdInfo />;
      break;
    case "company_products":
      icon = <GridIcon />;
      break;
    case "products":
      icon = <GridIcon />;
      break;
    case "contracts":
      icon = <PeopleRuleIcon />;
      break;
    case "location":
      icon = <LocationIcon />;
      break;
    default:
      icon = <OthersIcon />;
      break;
  }
  return icon;
}

function dbDateToCalender(inputDate: any) {
  if (!inputDate || typeof inputDate !== "string") return null;

  const formatted = inputDate.replace(/-/g, "/"); // string
  const parsed = Date.parse(formatted); // number

  if (isNaN(parsed)) return null;

  return new Date(parsed);
}

function calenderDateToDB(inputDate: any) {
  //parse the input date
  const date = new Date(inputDate);

  //extract the parts of the date
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  let format = "yyyy-MM-DD";
  //replace the month
  format = format.replace("MM", month.toString().padStart(2, "0"));

  //replace the year
  if (format.indexOf("yyyy") > -1) {
    format = format.replace("yyyy", year.toString());
  } else if (format.indexOf("yy") > -1) {
    format = format.replace("yy", year.toString().substr(2, 2));
  }

  //replace the day
  format = format.replace("DD", day.toString().padStart(2, "0"));

  return format;
}

function getPrimaryAddress(respData: any) {
  let PrimeAddress = respData.addresses.find(
    (address: any) => address.is_primary_address == 1
  );
  return PrimeAddress;
}

function dateFormat(inputDate: any, format: string) {
  //parse the input date
  const date = new Date(inputDate);

  //extract the parts of the date
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  //replace the month
  format = format.replace("MM", month.toString().padStart(2, "0"));

  //replace the year
  if (format.indexOf("yyyy") > -1) {
    format = format.replace("yyyy", year.toString());
  } else if (format.indexOf("yy") > -1) {
    format = format.replace("yy", year.toString().substr(2, 2));
  }

  //replace the day
  format = format.replace("dd", day.toString().padStart(2, "0"));

  return format;
}

function isWebResolutionSize() {
  return window.innerWidth > 768;
}

function getDaysDuration() {
  let daysDuration = [...orderDayTypeObj.all.daysOptions];
}

function isAlpha(value: string) {
  return value && IS_ALPHA_REGEX.test(value);
}

function isAlphaNumeric(value: string) {
  return value && IS_ALPHANUMERIC_REGEX.test(value);
}

function isAlphaNumericWithSpace(value: string) {
  return value && IS_ALPHANUMERIC_WITH_SPACE_REGEX.test(value);
}

function isAlphaNumericWithDash(value: string) {
  return value && IS_ALPHANUMERIC_WITH_DASH_REGEX.test(value);
}

function isAlphaNumericDashSpace(value: string) {
  return value && IS_ALPHANUMERIC_WITH_DASH_SPACE_REGEX.test(value);
}

function isNonNegativeInteger(input: string): boolean {
  // Regular expression to check if the input is a non-negative integer
  const nonNegativeIntegerRegex = /^[0-9]+$/;

  // Test the input against the regular expression
  return nonNegativeIntegerRegex.test(input);
}

function isAlphaNumericWithSpecialChars(
  value: string,
  specialCharsArray: string[]
) {
  let specialChars = specialCharsArray.join("");
  const regex = new RegExp(
    "^(?!\\d+$)(?:[a-zA-Z0-9][a-zA-Z0-9" + specialChars + "]*)?$"
  );
  return value && value.match(regex);
}

function isAlphaWithSpecialChars(value: string, specialCharsArray: string[]) {
  let specialChars = specialCharsArray.join("");
  const regex = new RegExp(
    "^(?!\\d+$)(?:[a-zA-Z][a-zA-Z" + specialChars + "]*)?$"
  );
  return value && value.match(regex);
}

function timeStringToMinutes(timeStr) {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
}

function compareTimes(time1, time2) {
  const minutes1 = timeStringToMinutes(time1);
  const minutes2 = timeStringToMinutes(time2);

  if (minutes1 > minutes2) return 1;
  if (minutes1 < minutes2) return -1;
  if (minutes1 == minutes2) return 0;
}

export {
  isEmptyVal,
  getMenuIcon,
  isNotEmpty,
  formatDateOnly,
  displayField,
  displayLimitedField,
  displayAmountField,
  downloadImage,
  displayDateField,
  displayDateTimeField,
  displayTimeField,
  displayDayDateField,
  displayDateMonthField,
  displayYesNo,
  generateTimeOptions,
  displayDayDateTimeField,
  processMinute,
  processSecond,
  addLabelValue,
  isWebResolutionSize,
  getCurrenciesFromCountryList,
  getStdCodesFromCountryList,
  isAlpha,
  isAlphaNumeric,
  getImageForTheme,
  isAlphaNumericWithSpace,
  isAlphaNumericWithDash,
  isAlphaNumericDashSpace,
  isAlphaNumericWithSpecialChars,
  isAlphaWithSpecialChars,
  getFoodTypeLabel,
  getStatusLabel,
  getSizeLabel,
  getAvailableDaysLabel,
  processProductTypeFields,
  isNotNull,
  dateFormat,
  getHourMinuteFormat,
  dbDateToCalender,
  calenderDateToDB,
  getPrimaryAddress,
  getLabelFor,
  getMultiLabelFor,
  getDaysDuration,
  getPincodeForArea,
  getStdCodeForCountry,
  zeroFill,
  compareTimes,
  removeHTMLTags,
  isNonNegativeInteger,
};
